import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { useTranslation } from 'react-i18next'
import { list } from '../data/Data'
import './rent.scss'
import { Carousel, Modal, Button, Skeleton, Row, Col, Divider, Badge } from 'antd'
import { getRoom, idleRooms } from "../home/room/redux/roomSlice"
import { AppDispatch, RootState } from '../../store'
import { useNavigate } from "react-router-dom"
import { ZoomInOutlined, TeamOutlined, MailOutlined } from '@ant-design/icons'
import PageHeader from "../common/pageHeader/PageHeader"

const getRoomTypeName = (type: number | undefined, t: (key: string) => string) => {
  switch (type) {
    case 0:
      return t('Big Double Room')
    case 1:
      return t('Double Room')
    case 2:
      return t('Master Room')
    case 3:
      return t('Lodging')
    case 4:
      return t('Family Room')
    case 5:
      return t('Big Simple Room')
    default:
      return t('Simple Room')
  }
}

const Rent = () => {
  const { t, i18n } = useTranslation('rooms')
  const { id } = useParams<{ id: string }>()
  const dispatch: AppDispatch = useDispatch()
  const apiURL = process.env.REACT_APP_API_URL
  const roomFromRedux = useSelector((state: RootState) => state.room.room)
  const getRoomStatus = useSelector((state: RootState) => state.room.getRoomStatus)
  const [imageSrc, setImageSrc] = useState<string[]>([])
  const [name, setName] = useState<string>()
  const [type, setType] = useState<string>()
  const [availability, setAvailability] = useState<string>()
  const [active, setActive] = useState<boolean>(false)
  const [capacity, setCapacity] = useState<number>()
  const [previewVisible, setPreviewVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const navigate = useNavigate()
  const path = 'https://drive.google.com/file/d/1p7dBNLUvyCOzfJJGTNP1bRgB2kp6jOv1/view?usp=sharing'

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [id])

  useEffect(() => {
    if (apiURL !== "https://aucoeur-back-main.onrender.com" && id) {
      dispatch(getRoom({ roomId: id }))
    }
    return () => {
      dispatch(idleRooms())
    }
  }, [dispatch, apiURL, id])

  useEffect(() => {
    const room = list.find(val => val._id === id)
    const item = apiURL === "https://aucoeur-back-main.onrender.com" ? room : roomFromRedux
    if (item && id) {
      setType(getRoomTypeName(item.type, t))
      setName(item.name)
      setActive(item.active)
      setAvailability(item.active ? t('Available') as string : t('Unavailable') as string)
      setCapacity(item.capacity)
      setImageSrc(item.cover ? (item.additionalImages ? [item.cover, ...item.additionalImages] : [item.cover]) : ['../images/about.jpg'])
    } else {
      setImageSrc(['../images/about.jpg'])
      setName('Unknown')
      setAvailability('Unknown')
      setCapacity(0)
    }
  }, [id, t, i18n.language, roomFromRedux, apiURL])

  const handlePreview = (src: string, index: number) => {
    setPreviewImage(src)
    setCurrentImageIndex(index)
    setPreviewVisible(true)
  }

  const redirectToServicePage = (event: React.MouseEvent<HTMLElement>, path: string) => {
    event.preventDefault()
    if (path !== "") {
      if (path.startsWith("https")) {
        window.open(path, '_blank')
      } else {
        navigate(path)
        window.scrollTo(0, 0)
      }
    }
  }

  const LoadingSkeleton = () => {
    return (
      <>
        <Col xs={24} lg={14}>
          <Skeleton active />
        </Col>
        <Col xs={24} lg={10}>
          <Skeleton active paragraph={{ rows: 5 }} />
        </Col>
      </>
    )
  }

  const ShowProduct = () => {
    if (!name) return null
    return (
      <>
        {/* Gallery Section */}
        <Col xs={24} lg={14}>
          <div className="room-gallery">
            <div className="main-carousel">
              <Carousel
                autoplay
                draggable
                arrows
              >
                {imageSrc.map((src, index) => (
                  <div key={index} className="carousel-slide" onClick={() => handlePreview(src, index)}>
                    <img 
                      src={src} 
                      alt={`${name} - ${index + 1}`}
                      className="carousel-image"
                    />
                    <div className="image-overlay">
                      <ZoomInOutlined className="zoom-icon" />
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>

            {imageSrc.length > 1 && (
              <div className="thumbnail-gallery">
                {imageSrc.map((src, index) => (
                  <div 
                    key={index} 
                    className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                    onClick={() => handlePreview(src, index)}
                  >
                    <img src={src} alt={`Thumbnail ${index + 1}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Col>

        {/* Details Section */}
        <Col xs={24} lg={10}>
          <div className="room-info-container">
            {/* Status Badge */}
            <div className="status-section">
              <Badge
                status={active ? "success" : "error"}
                text={
                  <span className={`status-text ${active ? 'available' : 'unavailable'}`}>
                    {availability}
                  </span>
                }
              />
            </div>

            <Divider style={{ margin: '12px 0' }} />

            {/* Title and Type */}
            <div className="header-section">
              <h1 className="room-name">{name}</h1>
              <p className="room-category">{type}</p>
            </div>

            <Divider style={{ margin: '20px 0' }} />

            {/* Rating and Info */}
            <div className="features-section">
              <div className="feature-item">
                <div className="feature-icon">
                  <TeamOutlined />
                </div>
                <div className="feature-content">
                  <p className="feature-label">{t('Capacity')}</p>
                  <p className="feature-value">{capacity} {capacity === 1 ? t('person') : t('persons')}</p>
                </div>
              </div>
              <Divider style={{ margin: '16px 0' }} />
            </div>

            {/* Action Buttons */}
            <div className="action-section">
              <Button
                type="primary"
                size="large"
                block
                className="primary-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  redirectToServicePage(e as React.MouseEvent<HTMLElement>, path)
                }}
              >
                {t('For more information')}
              </Button>
              <Button
                type="default"
                size="large"
                block
                className="secondary-btn"
                icon={<MailOutlined />}
                onClick={(e) => redirectToServicePage(e as React.MouseEvent<HTMLElement>, "/contact")}
              >
                {t('Contact us now', { ns: 'common' })}
              </Button>
            </div>
          </div>
        </Col>

        {/* Image Preview Modal */}
        <Modal
          className="image-preview-modal"
          open={previewVisible}
          footer={null}
          onCancel={() => setPreviewVisible(false)}
          width="90vw"
        >
          <div className="preview-container">
            <img alt="preview" src={previewImage} />
            {imageSrc.length > 1 && (
              <div className="preview-counter">
                {currentImageIndex + 1} / {imageSrc.length}
              </div>
            )}
          </div>
        </Modal>
      </>
    )
  }

  return (
    <div className="rent-page">
      <PageHeader
        label={t('Our rooms') as string}
        title={(name || t('Our cozy rooms')) as string}
        subtitle={type}
      />

      <div className="rent-content">
        <div className="container">
          <Row gutter={[48, 32]} align="stretch">
            {getRoomStatus === 'loading' ? <LoadingSkeleton /> : <ShowProduct />}
          </Row>
        </div>
      </div>
    </div>
  )
}

export default Rent
